<div class="grid md:grid-cols-2 gap-6 w-full p-2 lg:p-4">

  <!-- WEIGHING SCALE SETTINGS FORM -->
  <div class="p-6 bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition">
    <h3
      class="text-base sm:text-lg lg:text-xl font-semibold text-center bg-gradient-to-r from-[#c19725] to-[#e4b74a] text-transparent bg-clip-text mb-6">
      Weighing Scale Configuration
    </h3>

    <form [formGroup]="settingsForm" (ngSubmit)="onSubmit()" class="space-y-5">

      <!-- Enable / Disable Switch -->
      <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
        <div>
          <label class="text-sm md:text-base font-semibold text-gray-800">Enable Weighing Scale</label>
          <p class="text-xs text-gray-500">Allow sells panel to fetch weights from hardware</p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" formControlName="enabled" class="sr-only peer">
          <div
            class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c19725]">
          </div>
        </label>
      </div>

      <!-- COM Port Option -->
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1">COM Port / Serial Device</label>
        <div class="flex gap-2">
          <select formControlName="com_port"
            class="block w-full px-3 py-2 text-sm text-gray-800 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#c19725] focus:border-[#c19725]">
            <option value="BROWSER_DIRECT">Direct Browser Connection (No Agent Required)</option>
            <option value="SIMULATE">SIMULATE (Mock hardware mode)</option>

            <!-- Dynamically detected ports -->
            <option *ngFor="let port of detectedPorts" [value]="port.path">
              {{ port.path }} ({{ port.manufacturer || 'Unknown Hardware' }})
            </option>

            <!-- Fallback option for the currently saved port if not in detectedPorts -->
            <option *ngIf="isCurrentPortNotPresent()" [value]="savedComPort">
              {{ savedComPort }} (Saved Device)
            </option>
          </select>

          <button type="button"
            (click)="scanLocalPorts()"
            class="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 px-3 py-2 rounded-lg text-sm text-gray-700 font-medium transition">
            <i class="fa fa-sync-alt"></i> Scan
          </button>
        </div>
      </div>

      <!-- Direct Web Serial Connect Button -->
      <div *ngIf="settingsForm.get('com_port')?.value === 'BROWSER_DIRECT' && settingsForm.get('enabled')?.value"
        class="p-3 bg-[#fff9e6] border border-[#ffe0b3] rounded-lg flex flex-col gap-2">
        <p class="text-xs text-amber-800">
          <strong>Zero-Install Mode Active:</strong> Click the button below to pair your weighing scale directly in
          Google Chrome/Edge.
        </p>
        <button type="button" (click)="connectDirectly()"
          class="w-full bg-[#c19725] hover:bg-[#a6801c] text-white py-2 px-4 rounded-lg text-sm font-semibold transition shadow-md flex items-center justify-center gap-1">
          <i class="fa fa-plug"></i> Pair / Connect Weighing Scale
        </button>
      </div>

      <!-- Data Rate & Calibration -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Baud Rate (Data Rate)</label>
          <select formControlName="data_rate"
            class="block w-full px-3 py-2 text-sm text-gray-800 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#c19725] focus:border-[#c19725]">
            <option [ngValue]="2400">2400</option>
            <option [ngValue]="9600">9600</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Calibration Divisor</label>
          <input type="number" formControlName="calibration"
            class="block w-full px-3 py-2 text-sm text-gray-800 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#c19725] focus:border-[#c19725]" />
        </div>
      </div>

      <!-- Start & End String Markers -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Start Character/String</label>
          <input type="text" formControlName="start_string" placeholder="e.g. \u0002"
            class="block w-full px-3 py-2 text-sm text-gray-800 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#c19725] focus:border-[#c19725]" />
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">End Character/String</label>
          <input type="text" formControlName="end_string" placeholder="e.g. \r"
            class="block w-full px-3 py-2 text-sm text-gray-800 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#c19725] focus:border-[#c19725]" />
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex justify-center pt-2">
        <button type="submit"
          class="w-full bg-gradient-to-r from-[#c19725] to-[#e4b74a] text-white font-semibold text-sm py-2.5 rounded-lg shadow hover:opacity-90 transition">
          Save Settings
        </button>
      </div>
    </form>
  </div>

  <!-- LOCAL DEVICE AGENT STATUS CARD -->
  <div class="space-y-6">

    <!-- Local Monitor Card -->
    <div class="p-6 bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition">
      <h3
        class="text-base sm:text-lg lg:text-xl font-semibold text-center bg-gradient-to-r from-[#c19725] to-[#e4b74a] text-transparent bg-clip-text mb-6">
        {{ settingsForm.get('com_port')?.value === 'BROWSER_DIRECT' ? 'Browser Connection Monitor' : 'Local Agent Monitor' }}
      </h3>

      <div class="flex flex-col items-center justify-center space-y-4">
        <!-- Status Indicator Badge -->
        <div class="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" [ngClass]="{
            'bg-green-50 border-green-200 text-green-700': agentStatus === 'Online',
            'bg-red-50 border-red-200 text-red-700': agentStatus === 'Offline'
          }">
          <span class="w-3.5 h-3.5 rounded-full inline-block animate-pulse" [ngClass]="{
              'bg-green-600': agentStatus === 'Online',
              'bg-red-600': agentStatus === 'Offline'
            }"></span>
          {{ settingsForm.get('com_port')?.value === 'BROWSER_DIRECT' ? 'Browser Serial' : 'Hardware Agent' }}: {{ agentStatus }}
        </div>

        <!-- Live Parsed Weight Display -->
        <div class="w-full text-center p-5 bg-gray-50 border border-gray-100 rounded-xl">
          <p class="text-xs text-gray-500 uppercase tracking-wider font-semibold">Live Weight Value</p>
          <p class="text-4xl font-extrabold text-gray-800 mt-1">{{ liveAgentWeight | number:'1.3-3' }} <span
              class="text-lg font-normal text-gray-500">g</span></p>
          <p class="text-xs text-gray-400 mt-1" *ngIf="agentDetails?.com_port">Active Device: {{ agentDetails.com_port }}</p>
        </div>

        <!-- Only show Refresh Agent if not using Browser Direct -->
        <button type="button" *ngIf="settingsForm.get('com_port')?.value !== 'BROWSER_DIRECT'" (click)="initializeLocalAgent()"
          class="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold text-sm py-2.5 rounded-lg transition">
          <i class="fa fa-plug"></i> Refresh Agent Integration
        </button>
      </div>
    </div>

    <!-- SIMULATED MOCK HARDWARE SETUP (FOR TESTING & DEVELOPMENT) -->
    <div class="p-6 bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition"
      *ngIf="settingsForm.get('com_port')?.value === 'SIMULATE'">
      <h3
        class="text-base sm:text-lg lg:text-xl font-semibold text-center bg-gradient-to-r from-[#c19725] to-[#e4b74a] text-transparent bg-clip-text mb-4">
        Mock Hardware Control Panel
      </h3>
      <p class="text-xs text-gray-500 text-center mb-6">
        Simulate a physical weighing scale by locking specific weight values.
      </p>

      <div class="space-y-4">
        <!-- Toggle mock mode -->
        <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
          <div>
            <label class="text-sm font-semibold text-gray-800">Lock Target Weight</label>
            <p class="text-xs text-gray-500">Inject static value instead of random fluctuation</p>
          </div>
          <button type="button" (click)="toggleMockWeightMode()"
            class="px-4 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition border" [ngClass]="{
              'bg-[#c19725] border-[#c19725] text-white': isMockWeightActive,
              'bg-white border-gray-300 text-gray-700': !isMockWeightActive
            }">
            {{ isMockWeightActive ? 'LOCKED' : 'DYNAMIC' }}
          </button>
        </div>

        <!-- Slider and Input to adjust mock value -->
        <div class="space-y-2" *ngIf="isMockWeightActive">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-gray-700">Weight Value (g)</span>
            <input type="number" step="0.001" min="0" max="500" [(ngModel)]="mockWeightValue" (change)="setMockWeight()"
              class="w-24 text-right px-2 py-1 text-sm border rounded-md focus:outline-none focus:border-[#c19725]" />
          </div>
          <input type="range" min="0" max="250" step="0.01" [(ngModel)]="mockWeightValue" (input)="setMockWeight()"
            class="w-full accent-[#c19725]" />
        </div>
      </div>
    </div>
  </div>
</div>